import React, { useState, useEffect } from 'react';
import { useGetSoleConfigQuery, useSaveSoleConfigMutation } from 'app/createApi';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import { selectProjectPath, selectBranch } from 'app/sharedSlice';
import { selectDataProductName, setSoleOutput } from 'features/data-product-builder/reducers/builderSlice';
import StepWrapper from 'components/StepWrapper';
import LoadingAndErrorSection from 'components/LoadingAndErrorSection';
import SoleObjectCounts from '../components/SoleObjectCounts';
import { DatabaseConfigItem } from '../components/ConfigTreeItems';
import ConfigEditorModal, { type IEditorModalConfig } from '../components/ConfigEditorModal';
import AddObjectsFromDDLModal from '../components/AddObjectsFromDDLModal';
import { convertFlatToNestedFormat, convertNestedToFlatFormat } from '../utils/helperFunctions';
import type {
  IColumnConfig,
  IFlatConfigFormat,
  INestedConfigFormat,
  IDatabaseConfig,
  ISchemaConfig,
  ITableConfig,
  IViewConfig,
  IProcedureConfig,
  IFunctionConfig,
  IFileFormatConfig,
  ISequenceConfig,
  IObjectType,
} from '../utils/configEditorTypes';
import { parse, stringify } from 'yaml';
import { cloneDeep } from 'lodash';

export interface SoleConfigEditorStepProps {
  onBack: () => void;
  onContinue: () => void;
}

export default function SoleConfigEditorStep(props: SoleConfigEditorStepProps): JSX.Element {
  const dispatch = useAppDispatch();

  const projectPath = useAppSelector(selectProjectPath);
  const branch = useAppSelector(selectBranch);
  const dataProductName = useAppSelector(selectDataProductName);

  const [loading, setLoading] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('Failed to generate config');

  const [nestedSoleConfig, setNestedSoleConfig] = useState<INestedConfigFormat>();
  const [isEditorModalOpen, setIsEditorModalOpen] = useState(false);
  const [editorModalConfig, setEditorModalConfig] = useState<IEditorModalConfig>();
  const [isDDLModalOpen, setIsDDLModalOpen] = useState(false);

  const { data: soleConfigString, isLoading } = useGetSoleConfigQuery({
    projectPath,
    dataProductName,
    branch,
  });

  const [saveSoleConfig] = useSaveSoleConfigMutation();

  console.log('nestedSoleConfig:', nestedSoleConfig);

  useEffect(() => {
    if (soleConfigString !== undefined && soleConfigString !== null) {
      const parsedFlatSoleConfig = parse(soleConfigString) as IFlatConfigFormat;
      setNestedSoleConfig(convertFlatToNestedFormat(parsedFlatSoleConfig));
    }
  }, [soleConfigString]);

  // Cannot add a database for now
  const onAddObject = (
    objectType: 'schema' | 'table' | 'view' | 'procedure' | 'function' | 'file_format' | 'sequence' | 'column',
    objectData:
      | ISchemaConfig
      | ITableConfig
      | IViewConfig
      | IProcedureConfig
      | IFunctionConfig
      | IFileFormatConfig
      | ISequenceConfig
      | IColumnConfig,
  ): void => {
    if (nestedSoleConfig === undefined) return;

    const updatedConfig = cloneDeep(nestedSoleConfig);

    switch (objectType) {
      case 'schema': {
        const schemaData = objectData as ISchemaConfig;
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === schemaData.database);

        if (dbIndex !== -1) {
          updatedConfig.databases[dbIndex].schemas.push({
            ...schemaData,
            tables: [],
            views: [],
            procedures: [],
            functions: [],
            file_formats: [],
            sequences: [],
          });
        }
        break;
      }
      case 'table': {
        const tableData = objectData as ITableConfig;
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === tableData.database);

        if (dbIndex !== -1) {
          const schemaIndex = updatedConfig.databases[dbIndex].schemas.findIndex(
            (schema) => schema.name === tableData.schema,
          );

          if (schemaIndex !== -1) {
            updatedConfig.databases[dbIndex].schemas[schemaIndex].tables.push(tableData);
          }
        }
        break;
      }
      case 'view': {
        const viewData = objectData as IViewConfig;
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === viewData.database);

        if (dbIndex !== -1) {
          const schemaIndex = updatedConfig.databases[dbIndex].schemas.findIndex(
            (schema) => schema.name === viewData.schema,
          );

          if (schemaIndex !== -1) {
            updatedConfig.databases[dbIndex].schemas[schemaIndex].views.push(viewData);
          }
        }
        break;
      }
      case 'procedure': {
        const procedureData = objectData as IProcedureConfig;
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === procedureData.database);

        if (dbIndex !== -1) {
          const schemaIndex = updatedConfig.databases[dbIndex].schemas.findIndex(
            (schema) => schema.name === procedureData.schema,
          );

          if (schemaIndex !== -1) {
            updatedConfig.databases[dbIndex].schemas[schemaIndex].procedures.push(procedureData);
          }
        }
        break;
      }
      case 'function': {
        const functionData = objectData as IFunctionConfig;
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === functionData.database);

        if (dbIndex !== -1) {
          const schemaIndex = updatedConfig.databases[dbIndex].schemas.findIndex(
            (schema) => schema.name === functionData.schema,
          );

          if (schemaIndex !== -1) {
            updatedConfig.databases[dbIndex].schemas[schemaIndex].functions.push(functionData);
          }
        }
        break;
      }
      case 'file_format': {
        const fileFormatData = objectData as IFileFormatConfig;
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === fileFormatData.database);

        if (dbIndex !== -1) {
          const schemaIndex = updatedConfig.databases[dbIndex].schemas.findIndex(
            (schema) => schema.name === fileFormatData.schema,
          );

          if (schemaIndex !== -1) {
            updatedConfig.databases[dbIndex].schemas[schemaIndex].file_formats.push(fileFormatData);
          }
        }
        break;
      }
      case 'sequence': {
        const sequenceData = objectData as ISequenceConfig;
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === sequenceData.database);

        if (dbIndex !== -1) {
          const schemaIndex = updatedConfig.databases[dbIndex].schemas.findIndex(
            (schema) => schema.name === sequenceData.schema,
          );

          if (schemaIndex !== -1) {
            updatedConfig.databases[dbIndex].schemas[schemaIndex].sequences.push(sequenceData);
          }
        }
        break;
      }
      case 'column': {
        const columnData = objectData as IColumnConfig;
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === columnData.database);

        if (dbIndex !== -1) {
          const schemaIndex = updatedConfig.databases[dbIndex].schemas.findIndex(
            (schema) => schema.name === columnData.schema,
          );

          if (schemaIndex !== -1) {
            const tableIndex = updatedConfig.databases[dbIndex].schemas[schemaIndex].tables.findIndex(
              (table) => table.name === columnData.table,
            );

            if (tableIndex !== -1) {
              updatedConfig.databases[dbIndex].schemas[schemaIndex].tables[tableIndex].columns.push(columnData);
            }
          }
        }
        break;
      }
    }

    setNestedSoleConfig(updatedConfig);
    setIsEditorModalOpen(false);
  };

  const onEditObject = (
    objectType: IObjectType,
    objectData:
      | IDatabaseConfig
      | ISchemaConfig
      | ITableConfig
      | IViewConfig
      | IProcedureConfig
      | IFunctionConfig
      | IFileFormatConfig
      | ISequenceConfig
      | IColumnConfig,
    originalName: string,
  ): void => {
    if (nestedSoleConfig === undefined) return;

    const updatedConfig = cloneDeep(nestedSoleConfig);

    switch (objectType) {
      case 'database': {
        const dbData = objectData as any; // Using any since IDatabaseConfig is not in the function signature
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === originalName);

        if (dbIndex !== -1) {
          updatedConfig.databases[dbIndex] = {
            ...updatedConfig.databases[dbIndex],
            ...dbData,
          };
        }
        break;
      }
      case 'schema': {
        const schemaData = objectData as ISchemaConfig;
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === schemaData.database);

        if (dbIndex !== -1) {
          const schemaIndex = updatedConfig.databases[dbIndex].schemas.findIndex(
            (schema) => schema.name === originalName,
          );

          if (schemaIndex !== -1) {
            updatedConfig.databases[dbIndex].schemas[schemaIndex] = {
              ...updatedConfig.databases[dbIndex].schemas[schemaIndex],
              ...schemaData,
            };
          }
        }
        break;
      }
      case 'table': {
        const tableData = objectData as ITableConfig;
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === tableData.database);

        if (dbIndex !== -1) {
          const schemaIndex = updatedConfig.databases[dbIndex].schemas.findIndex(
            (schema) => schema.name === tableData.schema,
          );

          if (schemaIndex !== -1) {
            const tableIndex = updatedConfig.databases[dbIndex].schemas[schemaIndex].tables.findIndex(
              (table) => table.name === originalName,
            );

            if (tableIndex !== -1) {
              updatedConfig.databases[dbIndex].schemas[schemaIndex].tables[tableIndex] = {
                ...updatedConfig.databases[dbIndex].schemas[schemaIndex].tables[tableIndex],
                ...tableData,
              };
            }
          }
        }
        break;
      }
      case 'view': {
        const viewData = objectData as IViewConfig;
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === viewData.database);

        if (dbIndex !== -1) {
          const schemaIndex = updatedConfig.databases[dbIndex].schemas.findIndex(
            (schema) => schema.name === viewData.schema,
          );

          if (schemaIndex !== -1) {
            const viewIndex = updatedConfig.databases[dbIndex].schemas[schemaIndex].views.findIndex(
              (view) => view.name === originalName,
            );

            if (viewIndex !== -1) {
              updatedConfig.databases[dbIndex].schemas[schemaIndex].views[viewIndex] = {
                ...updatedConfig.databases[dbIndex].schemas[schemaIndex].views[viewIndex],
                ...viewData,
              };
            }
          }
        }
        break;
      }
      case 'column': {
        const columnData = objectData as IColumnConfig;
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === columnData.database);

        if (dbIndex !== -1) {
          const schemaIndex = updatedConfig.databases[dbIndex].schemas.findIndex(
            (schema) => schema.name === columnData.schema,
          );

          if (schemaIndex !== -1) {
            const tableIndex = updatedConfig.databases[dbIndex].schemas[schemaIndex].tables.findIndex(
              (table) => table.name === columnData.table,
            );

            if (tableIndex !== -1) {
              const columnIndex = updatedConfig.databases[dbIndex].schemas[schemaIndex].tables[
                tableIndex
              ].columns.findIndex((column) => column.name === originalName);

              if (columnIndex !== -1) {
                updatedConfig.databases[dbIndex].schemas[schemaIndex].tables[tableIndex].columns[columnIndex] = {
                  ...updatedConfig.databases[dbIndex].schemas[schemaIndex].tables[tableIndex].columns[columnIndex],
                  ...columnData,
                };
              }
            }
          }
        }
        break;
      }
      case 'procedure': {
        const procedureData = objectData as IProcedureConfig;
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === procedureData.database);

        if (dbIndex !== -1) {
          const schemaIndex = updatedConfig.databases[dbIndex].schemas.findIndex(
            (schema) => schema.name === procedureData.schema,
          );

          if (schemaIndex !== -1) {
            const procedureIndex = updatedConfig.databases[dbIndex].schemas[schemaIndex].procedures.findIndex(
              (procedure) => procedure.name === originalName,
            );

            if (procedureIndex !== -1) {
              updatedConfig.databases[dbIndex].schemas[schemaIndex].procedures[procedureIndex] = {
                ...updatedConfig.databases[dbIndex].schemas[schemaIndex].procedures[procedureIndex],
                ...procedureData,
              };
            }
          }
        }
        break;
      }
      case 'function': {
        const functionData = objectData as IFunctionConfig;
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === functionData.database);

        if (dbIndex !== -1) {
          const schemaIndex = updatedConfig.databases[dbIndex].schemas.findIndex(
            (schema) => schema.name === functionData.schema,
          );

          if (schemaIndex !== -1) {
            const functionIndex = updatedConfig.databases[dbIndex].schemas[schemaIndex].functions.findIndex(
              (func) => func.name === originalName,
            );

            if (functionIndex !== -1) {
              updatedConfig.databases[dbIndex].schemas[schemaIndex].functions[functionIndex] = {
                ...updatedConfig.databases[dbIndex].schemas[schemaIndex].functions[functionIndex],
                ...functionData,
              };
            }
          }
        }
        break;
      }
      case 'file_format': {
        const fileFormatData = objectData as IFileFormatConfig;
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === fileFormatData.database);

        if (dbIndex !== -1) {
          const schemaIndex = updatedConfig.databases[dbIndex].schemas.findIndex(
            (schema) => schema.name === fileFormatData.schema,
          );

          if (schemaIndex !== -1) {
            const fileFormatIndex = updatedConfig.databases[dbIndex].schemas[schemaIndex].file_formats.findIndex(
              (fileFormat) => fileFormat.name === originalName,
            );

            if (fileFormatIndex !== -1) {
              updatedConfig.databases[dbIndex].schemas[schemaIndex].file_formats[fileFormatIndex] = {
                ...updatedConfig.databases[dbIndex].schemas[schemaIndex].file_formats[fileFormatIndex],
                ...fileFormatData,
              };
            }
          }
        }
        break;
      }
      case 'sequence': {
        const sequenceData = objectData as ISequenceConfig;
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === sequenceData.database);

        if (dbIndex !== -1) {
          const schemaIndex = updatedConfig.databases[dbIndex].schemas.findIndex(
            (schema) => schema.name === sequenceData.schema,
          );

          if (schemaIndex !== -1) {
            const sequenceIndex = updatedConfig.databases[dbIndex].schemas[schemaIndex].sequences.findIndex(
              (sequence) => sequence.name === originalName,
            );

            if (sequenceIndex !== -1) {
              updatedConfig.databases[dbIndex].schemas[schemaIndex].sequences[sequenceIndex] = {
                ...updatedConfig.databases[dbIndex].schemas[schemaIndex].sequences[sequenceIndex],
                ...sequenceData,
              };
            }
          }
        }
        break;
      }
    }

    setNestedSoleConfig(updatedConfig);
    setIsEditorModalOpen(false);
  };

  const onDeleteObject = (
    objectType: IObjectType,
    objectData:
      | IDatabaseConfig
      | ISchemaConfig
      | ITableConfig
      | IViewConfig
      | IProcedureConfig
      | IFunctionConfig
      | IFileFormatConfig
      | ISequenceConfig
      | IColumnConfig,
  ): void => {
    if (nestedSoleConfig === undefined) return;

    const updatedConfig = cloneDeep(nestedSoleConfig);

    switch (objectType) {
      case 'database': {
        const dbData = objectData as IDatabaseConfig;
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === dbData.name);

        if (dbIndex !== -1) {
          updatedConfig.databases.splice(dbIndex, 1);
        }
        break;
      }
      case 'schema': {
        const schemaData = objectData as ISchemaConfig;
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === schemaData.database);

        if (dbIndex !== -1) {
          const schemaIndex = updatedConfig.databases[dbIndex].schemas.findIndex(
            (schema) => schema.name === schemaData.name,
          );

          if (schemaIndex !== -1) {
            updatedConfig.databases[dbIndex].schemas.splice(schemaIndex, 1);
          }
        }
        break;
      }
      case 'table': {
        const tableData = objectData as ITableConfig;
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === tableData.database);

        if (dbIndex !== -1) {
          const schemaIndex = updatedConfig.databases[dbIndex].schemas.findIndex(
            (schema) => schema.name === tableData.schema,
          );

          if (schemaIndex !== -1) {
            const tableIndex = updatedConfig.databases[dbIndex].schemas[schemaIndex].tables.findIndex(
              (table) => table.name === tableData.name,
            );

            if (tableIndex !== -1) {
              updatedConfig.databases[dbIndex].schemas[schemaIndex].tables.splice(tableIndex, 1);
            }
          }
        }
        break;
      }
      case 'view': {
        const viewData = objectData as IViewConfig;
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === viewData.database);

        if (dbIndex !== -1) {
          const schemaIndex = updatedConfig.databases[dbIndex].schemas.findIndex(
            (schema) => schema.name === viewData.schema,
          );

          if (schemaIndex !== -1) {
            const viewIndex = updatedConfig.databases[dbIndex].schemas[schemaIndex].views.findIndex(
              (view) => view.name === viewData.name,
            );

            if (viewIndex !== -1) {
              updatedConfig.databases[dbIndex].schemas[schemaIndex].views.splice(viewIndex, 1);
            }
          }
        }
        break;
      }
      case 'column': {
        const columnData = objectData as IColumnConfig;
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === columnData.database);

        if (dbIndex !== -1) {
          const schemaIndex = updatedConfig.databases[dbIndex].schemas.findIndex(
            (schema) => schema.name === columnData.schema,
          );

          if (schemaIndex !== -1) {
            const tableIndex = updatedConfig.databases[dbIndex].schemas[schemaIndex].tables.findIndex(
              (table) => table.name === columnData.table,
            );

            if (tableIndex !== -1) {
              const columnIndex = updatedConfig.databases[dbIndex].schemas[schemaIndex].tables[
                tableIndex
              ].columns.findIndex((column) => column.name === columnData.name);

              if (columnIndex !== -1) {
                updatedConfig.databases[dbIndex].schemas[schemaIndex].tables[tableIndex].columns.splice(columnIndex, 1);
              }
            }
          }
        }
        break;
      }
      case 'procedure': {
        const procedureData = objectData as IProcedureConfig;
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === procedureData.database);

        if (dbIndex !== -1) {
          const schemaIndex = updatedConfig.databases[dbIndex].schemas.findIndex(
            (schema) => schema.name === procedureData.schema,
          );

          if (schemaIndex !== -1) {
            const procedureIndex = updatedConfig.databases[dbIndex].schemas[schemaIndex].procedures.findIndex(
              (procedure) => procedure.name === procedureData.name,
            );

            if (procedureIndex !== -1) {
              updatedConfig.databases[dbIndex].schemas[schemaIndex].procedures.splice(procedureIndex, 1);
            }
          }
        }
        break;
      }
      case 'function': {
        const functionData = objectData as IFunctionConfig;
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === functionData.database);

        if (dbIndex !== -1) {
          const schemaIndex = updatedConfig.databases[dbIndex].schemas.findIndex(
            (schema) => schema.name === functionData.schema,
          );

          if (schemaIndex !== -1) {
            const functionIndex = updatedConfig.databases[dbIndex].schemas[schemaIndex].functions.findIndex(
              (func) => func.name === functionData.name,
            );

            if (functionIndex !== -1) {
              updatedConfig.databases[dbIndex].schemas[schemaIndex].functions.splice(functionIndex, 1);
            }
          }
        }
        break;
      }
      case 'file_format': {
        const fileFormatData = objectData as IFileFormatConfig;
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === fileFormatData.database);

        if (dbIndex !== -1) {
          const schemaIndex = updatedConfig.databases[dbIndex].schemas.findIndex(
            (schema) => schema.name === fileFormatData.schema,
          );

          if (schemaIndex !== -1) {
            const fileFormatIndex = updatedConfig.databases[dbIndex].schemas[schemaIndex].file_formats.findIndex(
              (fileFormat) => fileFormat.name === fileFormatData.name,
            );

            if (fileFormatIndex !== -1) {
              updatedConfig.databases[dbIndex].schemas[schemaIndex].file_formats.splice(fileFormatIndex, 1);
            }
          }
        }
        break;
      }
      case 'sequence': {
        const sequenceData = objectData as ISequenceConfig;
        const dbIndex = updatedConfig.databases.findIndex((db) => db.name === sequenceData.database);

        if (dbIndex !== -1) {
          const schemaIndex = updatedConfig.databases[dbIndex].schemas.findIndex(
            (schema) => schema.name === sequenceData.schema,
          );

          if (schemaIndex !== -1) {
            const sequenceIndex = updatedConfig.databases[dbIndex].schemas[schemaIndex].sequences.findIndex(
              (sequence) => sequence.name === sequenceData.name,
            );

            if (sequenceIndex !== -1) {
              updatedConfig.databases[dbIndex].schemas[schemaIndex].sequences.splice(sequenceIndex, 1);
            }
          }
        }
        break;
      }
    }

    setNestedSoleConfig(updatedConfig);
    setIsEditorModalOpen(false);
  };

  const onContinue = (): void => {
    if (nestedSoleConfig !== undefined) {
      setLoading(true);
      setFailed(false);
      const flattenedSoleConfig = convertNestedToFlatFormat(nestedSoleConfig);
      saveSoleConfig({
        projectPath,
        dataProductName,
        branch,
        sole: stringify(flattenedSoleConfig),
      })
        .unwrap()
        .then(() => {
          setFailed(false);
          dispatch(setSoleOutput(stringify(flattenedSoleConfig)));
          props.onContinue();
        })
        .catch((error) => {
          setFailed(true);
          setErrorMessage(error.data);
          console.error('Error saving SOLE config:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return (
    <StepWrapper
      title="Which Snowflake objects would you like to generate configurations for?"
      subtitle="Select Snowflake objects"
      onBack={() => props.onBack()}
      onContinue={() => onContinue()}
      isLoading={loading}
      isFailed={failed}
      errorMessage={errorMessage}
    >
      <div className="w-full flex flex-col items-center">
        <div className="w-full max-w-2xl">
          {isLoading ? (
            <div className="min-h-6 flex items-center justify-center">
              <div
                className="animate-spin inline-block w-6 h-6 border-[2px] border-current border-t-transparent text-dataops-primary rounded-full"
                role="status"
                aria-label="loading"
              >
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              <div className="flex justify-end mb-4">
                <button
                  className="rounded-full bg-white border border-dataops-secondary-blue px-2 py-1 text-sm shadow-sm text-dataops-secondary-blue hover:bg-dataops-secondary-blue/5"
                  onClick={() => setIsDDLModalOpen(true)}
                >
                  Add objects from DDL
                </button>
              </div>
              <SoleObjectCounts nestedSoleConfig={nestedSoleConfig} />
              <div className="mt-4 p-4">
                {nestedSoleConfig?.databases.map((database) => (
                  <DatabaseConfigItem
                    key={database.name}
                    database={database}
                    openEditorModal={() => setIsEditorModalOpen(true)}
                    setEditorModalConfig={setEditorModalConfig}
                    onDeleteObject={onDeleteObject}
                  />
                ))}
              </div>
            </>
          )}
        </div>
        <div className="min-w-[20rem] mx-[auto] mt-4">
          <LoadingAndErrorSection
            isLoading={loading}
            isFailed={failed}
            errorMessage={errorMessage}
            hideLoading={true}
          />
        </div>
      </div>
      <ConfigEditorModal
        open={isEditorModalOpen}
        onClose={() => setIsEditorModalOpen(false)}
        config={editorModalConfig}
        onAddObject={onAddObject}
        onEditObject={onEditObject}
      />
      <AddObjectsFromDDLModal
        open={isDDLModalOpen}
        onClose={() => setIsDDLModalOpen(false)}
        nestedSoleConfig={nestedSoleConfig}
        setNestedSoleConfig={setNestedSoleConfig}
      />
    </StepWrapper>
  );
}
